import { authMiddlewares } from '@/middlewares/auth.middlewares';
import { ecommerceMiddlewares } from '@/middlewares/ecommerce.middlewares';
import { useUiStore } from '@/stores/ui.store';
import { createRouter, createWebHistory } from 'vue-router';
import adminRoutes from '@/modules/admin/routes/admin.routes';
import npsRoutes from '@/modules/nps/routes/nps.routes';
import { AppType, useAppStore } from '@/stores/app.store';
import { EcommerceSettingsPlanType } from '@/interfaces/ecommerce.interface';
import { useEcommerceStore } from '@/stores/ecommerce.store';
import cleepoRoutes from '@/modules/cleepo/routes/cleepo.routes';
import { i18n } from '@/plugins/i18n';
import { useAuthStore } from '@/stores/auth.store';
import { useOrderSummaryStore } from '@/stores/orderSummary.store';
import { useStorage } from '@vueuse/core';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      component: () => import('../layouts/DashboardLayout.vue'),
      beforeEnter: [
        authMiddlewares.isAuthenticated,
        authMiddlewares.isAccountConfigured,
        authMiddlewares.isFlokeeAccount,
        () => {
          useAppStore().setCurrentApp(AppType.Flokee);
        },
      ],
      children: [
        {
          path: '',
          name: 'DashboardView',
          meta: { title: 'nav_menu.dashboard' },
          component: () => import('../views/common/DashboardView.vue'),
        },
        {
          path: 'products',
          name: 'ProductsView',
          meta: { title: 'nav_menu.products' },
          component: () => import('../views/common/ProductsView.vue'),
        },
        {
          path: 'sales',
          name: 'SalesView',
          meta: { title: 'nav_menu.sales' },
          component: () => import('../views/common/SalesView.vue'),
        },
      ],
    },
    {
      path: '/invest',
      component: () => import('../layouts/DashboardBlankLayout.vue'),
      meta: { title: 'nav_menu.investment' },
      beforeEnter: [
        authMiddlewares.isAuthenticated,
        authMiddlewares.isAccountConfigured,
        authMiddlewares.isFlokeeAccount,
        (to, from, next) => {
          useAppStore().setCurrentApp(AppType.Flokee);
          const ecommerceStore = useEcommerceStore();
          if (
            ecommerceStore.currentEcommerce?.settings.flokee_plan_type ===
            EcommerceSettingsPlanType.PAID
          ) {
            next({ name: 'AccountSettingsPlanView' });
          }
          next();
        },
      ],
      children: [
        {
          path: '',
          name: 'InvestView',
          component: () => import('../views/common/InvestView.vue'),
        },
      ],
    },
    {
      path: '/widgets',
      beforeEnter: [
        authMiddlewares.isAuthenticated,
        authMiddlewares.isAccountConfigured,
        authMiddlewares.isFlokeeAccount,
      ],
      component: () => import('../layouts/DashboardLayout.vue'),
      children: [
        {
          meta: { title: 'nav_menu.widgets' },
          path: '',
          name: 'WidgetsView',
          component: () => import('../views/widgets/WidgetsView.vue'),
        },
        {
          meta: { title: 'widgets.chatpop.title' },
          path: 'chatpop',
          name: 'WidgetsChatpopView',
          component: () => import('../views/widgets/WidgetsChatpopView.vue'),
        },
      ],
    },
    {
      path: '/store',
      component: () => import('../layouts/DashboardLayout.vue'),
      beforeEnter: [
        authMiddlewares.isAuthenticated,
        authMiddlewares.isAccountConfigured,
        authMiddlewares.isFlokeeAccount,
        () => {
          useAppStore().setCurrentApp(AppType.Flokee);
        },
      ],
      children: [
        {
          path: 'quality',
          name: 'StoreQualityView',
          meta: { title: 'nav_menu.store_quality' },
          component: () => import('../views/store/StoreQualityView.vue'),
        },
      ],
    },
    {
      path: '/user/settings',
      component: () => import('../layouts/UserSettingsLayout.vue'),
      beforeEnter: [authMiddlewares.isAuthenticated, authMiddlewares.isAccountConfigured],
      children: [
        {
          path: 'data',
          name: 'UserSettingsDataView',
          meta: { title: 'user_settings.nav_menu.personal_data' },
          component: () => import('../views/user-settings/UserSettingsDataView.vue'),
        },
        {
          path: 'security',
          name: 'UserSettingsSecurityView',
          meta: { title: 'user_settings.nav_menu.security' },
          component: () => import('../views/user-settings/UserSettingsSecurityView.vue'),
        },
      ],
    },
    {
      path: '/account/settings',
      component: () => import('../layouts/AccountSettingsLayout.vue'),
      beforeEnter: [authMiddlewares.isAuthenticated, authMiddlewares.isAccountConfigured],
      children: [
        {
          path: 'general',
          name: 'AccountSettingsGeneralView',
          meta: { title: 'account_settings.nav_menu.general' },
          component: () => import('../views/account-settings/AccountSettingsGeneralView.vue'),
        },
        {
          path: 'users',
          name: 'AccountSettingsUsersView',
          meta: { title: 'account_settings.nav_menu.users' },
          component: () => import('../views/account-settings/AccountSettingsUsersView.vue'),
        },
        {
          path: 'billing',
          name: 'AccountSettingsBillingView',
          meta: { title: 'account_settings.nav_menu.billing' },
          component: () => import('../views/account-settings/AccountSettingsBillingView.vue'),
        },
        {
          path: 'plan',
          meta: { title: 'account_settings.nav_menu.plan' },
          beforeEnter: [
            (to, from, next) => {
              const orderSummaryStore = useOrderSummaryStore();
              if (orderSummaryStore.hasFreePlanActive) {
                next({ name: 'InvestView' });
              }

              next();
            },
          ],
          children: [
            {
              path: '',
              name: 'AccountSettingsPlanView',
              component: () => import('../views/account-settings/AccountSettingsPlanView.vue'),
            },
            {
              path: 'change',
              name: 'AccountSettingsPlanChangeView',
              component: () =>
                import('../views/account-settings/AccountSettingsPlanChangeView.vue'),
            },
          ],
        },
      ],
    },
    {
      path: '/store/settings',
      component: () => import('../layouts/DashboardLayout.vue'),
      beforeEnter: [authMiddlewares.isAuthenticated, authMiddlewares.isAccountConfigured],
      redirect: '/store/settings/general',
      children: [
        {
          path: 'general',
          name: 'StoreSettingsGeneralView',
          meta: { title: 'store_settings.nav_menu.general' },
          component: () => import('../views/store-settings/StoreSettingsGeneralView.vue'),
        },
        {
          path: 'connections',
          name: 'StoreSettingsConnectionsView',
          meta: { title: 'store_settings.nav_menu.connections' },
          component: () => import('../views/store-settings/StoreSettingsConnectionsView.vue'),
        },
        {
          path: 'notifications',
          name: 'StoreSettingsNotificationsView',
          meta: { title: 'store_settings.nav_menu.notifications' },
          component: () => import('../views/store-settings/StoreSettingsNotificationsView.vue'),
        },
        {
          path: 'billing',
          name: 'StoreSettingsBillingView',
          beforeEnter: [ecommerceMiddlewares.isPaid],
          meta: { title: 'store_settings.nav_menu.billing' },
          component: () => import('../views/store-settings/StoreSettingsBillingView.vue'),
        },
      ],
    },
    {
      path: '/store/connections',
      component: () => import('../layouts/BlankLayout.vue'),
      beforeEnter: [authMiddlewares.isAuthenticated, authMiddlewares.isAccountConfigured],
      redirect: '/store/settings/connections',
      children: [
        {
          path: 'tag-manager',
          meta: { title: 'store_connections.tag_manager.setup.title' },
          beforeEnter: [ecommerceMiddlewares.isPaid],
          children: [
            {
              path: '',
              name: 'StoreConnectionsTagManagerSetupView',
              component: () =>
                import(
                  '../views/store-connections/tag-manager/StoreConnectionsTagManagerSetupView.vue'
                ),
            },
            {
              path: 'internal',
              name: 'StoreConnectionsTagManagerInternalView',
              component: () =>
                import(
                  '../views/store-connections/tag-manager/StoreConnectionsTagManagerInternalView.vue'
                ),
            },
            {
              path: 'external',
              name: 'StoreConnectionsTagManagerExternalView',
              component: () =>
                import(
                  '../views/store-connections/tag-manager/StoreConnectionsTagManagerExternalView.vue'
                ),
            },
          ],
        },
        {
          path: 'analytics',
          meta: { title: 'store_connections.analytics.setup.title' },
          children: [
            {
              path: '',
              name: 'StoreConnectionsAnalyticsSetupView',
              component: () =>
                import(
                  '../views/store-connections/analytics/StoreConnectionsAnalyticsSetupView.vue'
                ),
            },
            {
              path: 'internal',
              name: 'StoreConnectionsAnalyticsInternalView',
              component: () =>
                import(
                  '../views/store-connections/analytics/StoreConnectionsAnalyticsInternalView.vue'
                ),
            },
            {
              path: 'external',
              children: [
                {
                  path: '',
                  name: 'StoreConnectionsAnalyticsExternalView',
                  component: () =>
                    import(
                      '../views/store-connections/analytics/StoreConnectionsAnalyticsExternalView.vue'
                    ),
                },
                {
                  path: 'callback',
                  name: 'StoreConnectionsAnalyticsExternalCallbackView',
                  component: () =>
                    import(
                      '../views/store-connections/analytics/StoreConnectionsAnalyticsExternalCallbackView.vue'
                    ),
                },
              ],
            },
          ],
        },
        {
          path: 'merchant',
          meta: { title: 'store_connections.merchant.setup.title' },
          beforeEnter: [ecommerceMiddlewares.isPaid],
          children: [
            {
              path: '',
              name: 'StoreConnectionsMerchantSetupView',
              component: () =>
                import('../views/store-connections/merchant/StoreConnectionsMerchantSetupView.vue'),
            },
            {
              path: 'internal',
              name: 'StoreConnectionsMerchantInternalView',
              component: () =>
                import(
                  '../views/store-connections/merchant/StoreConnectionsMerchantInternalView.vue'
                ),
            },
            {
              path: 'external',
              name: 'StoreConnectionsMerchantExternalView',
              component: () =>
                import(
                  '../views/store-connections/merchant/StoreConnectionsMerchantExternalView.vue'
                ),
            },
          ],
        },
      ],
    },
    {
      path: '/auth',
      component: () => import('../layouts/BlankLayout.vue'),
      redirect: '/auth/login',
      meta: { title: 'auth.sign_in.title' },
      beforeEnter: [() => useAppStore().setCurrentApp(AppType.Flokee), authMiddlewares.isAppLogin],
      children: [
        {
          path: 'login',
          name: 'AuthLoginView',
          meta: { title: 'auth.sign_in.title' },
          beforeEnter: [authMiddlewares.shouldRedirectToDashboard],
          component: () => import('../views/auth/AuthLoginView.vue'),
        },
        {
          path: 'register',
          name: 'AuthRegisterView',
          meta: { title: 'auth.sign_up.title' },
          beforeEnter: [authMiddlewares.shouldRedirectToDashboard],
          component: () => import('../views/auth/AuthRegisterView.vue'),
        },
        {
          path: 'email',
          name: 'AuthEmailView',
          meta: { title: 'auth.sign_in_email.title' },
          beforeEnter: [authMiddlewares.shouldRedirectToDashboard],
          props: (route) => ({
            email: route.query.email ?? '',
          }),
          component: () => import('../views/auth/AuthEmailView.vue'),
        },
        {
          path: 'recover-password',
          meta: { title: 'auth.recover_password.title' },
          component: () => import('../layouts/BaseLayout.vue'),
          children: [
            {
              path: '',
              name: 'AuthRecoverPasswordView',
              props: (route) => ({
                email: route.query.email ?? '',
              }),
              component: () => import('../views/auth/AuthRecoverPasswordView.vue'),
            },
          ],
        },
        {
          path: 'reset-password',
          meta: { title: 'auth.reset_password.title' },
          component: () => import('../layouts/BaseLayout.vue'),
          children: [
            {
              path: '',
              name: 'AuthResetPasswordView',
              props: (route) => ({
                token: route.query.token ?? '',
              }),
              component: () => import('../views/auth/AuthResetPasswordView.vue'),
            },
          ],
        },
        {
          path: 'tienda-nube',
          component: () => import('../layouts/BaseLayout.vue'),
          beforeEnter: [authMiddlewares.verifyAuthentication],
          children: [
            {
              path: 'callback',
              name: 'AuthTiendanubeCallbackView',
              props: (route) => ({
                code: route.query.code ?? '',
              }),
              component: () => import('../views/auth/AuthTiendanubeCallbackView.vue'),
            },
          ],
        },
        {
          path: 'facebook',
          component: () => import('../layouts/BaseLayout.vue'),
          children: [
            {
              path: 'callback',
              name: 'AuthFacebookCallbackView',
              props: (route) => ({
                code: route.query.code ?? '',
              }),
              component: () => import('../views/auth/AuthFacebookCallbackView.vue'),
            },
          ],
        },
      ],
    },
    {
      path: '/wizard',
      component: () => import('../layouts/BlankLayout.vue'),
      redirect: '/',
      beforeEnter: [
        authMiddlewares.isAuthenticated,
        () => {
          useAppStore().setCurrentApp(AppType.Flokee);
        },
      ],
      children: [
        {
          path: 'create-account',
          name: 'WizardCreateAccountView',
          props: (route) => ({
            create: route.query.create,
          }),
          component: () => import('../views/wizard/WizardCreateAccountView.vue'),
        },
        {
          path: 'create-ecommerce',
          name: 'WizardCreateEcommerceView',
          component: () => import('../views/wizard/WizardCreateEcommerceView.vue'),
        },
        {
          path: 'select-ecommerce-provider',
          name: 'WizardSelectEcommerceProviderView',
          component: () => import('../views/wizard/WizardSelectEcommerceProviderView.vue'),
        },
        {
          path: 'import-ecommerce',
          name: 'WizardImportEcommerceView',
          component: () => import('../views/wizard/WizardImportEcommerceView.vue'),
        },
      ],
    },
    {
      path: '/select-account',
      component: () => import('../layouts/BlankLayout.vue'),
      beforeEnter: [authMiddlewares.isAuthenticated],
      meta: { title: 'select_account.description' },
      children: [
        {
          path: '',
          name: 'SelectAccountView',
          component: () => import('../views/common/SelectAccountView.vue'),
        },
      ],
    },
    {
      path: '/cart',
      component: () => import('../layouts/BlankLayout.vue'),
      redirect: '/cart/select',
      beforeEnter: [
        authMiddlewares.isAuthenticated,
        (to, from, next) => {
          const orderSummaryStore = useOrderSummaryStore();
          if (!orderSummaryStore.hasFreePlanActive)
            return next({ name: 'AccountSettingsPlanView' });
          return next();
        },
        (to, from, next) => {
          const authStore = useAuthStore();
          if (!authStore.isFlokeeAccount) return next();

          ecommerceMiddlewares.showPlanSubscriptionApprovalModal(to, from, next);
        },
      ],
      children: [
        {
          path: 'select',
          component: () => import('../layouts/BaseLayout.vue'),
          children: [
            {
              path: '',
              meta: { title: 'cart_select.meta_title' },
              name: 'CartSelectView',
              props: (route) => ({
                coupon: route.query.coupon ?? '',
              }),
              component: () => import('../views/cart/CartSelectView.vue'),
            },
          ],
        },
        {
          path: 'checkout/:cart_id',
          name: 'CartCheckoutView',
          meta: { title: 'cart_checkout.title' },
          props: (route) => ({
            cart_id: Number(route.params.cart_id),
            coupon: route.query.coupon ?? '',
          }),
          component: () => import('../views/cart/CartCheckoutView.vue'),
        },
      ],
    },
    {
      path: '/test',
      component: () => import('../layouts/BaseLayout.vue'),
      beforeEnter: [authMiddlewares.verifyAuthentication],
      children: [
        {
          path: 'pusher',
          name: 'TestPusherView',
          component: () => import('../views/test/TestPusherView.vue'),
        },
      ],
    },
    {
      path: '/admin',
      meta: { title: 'Admin' },
      beforeEnter: [authMiddlewares.verifyAuthentication, authMiddlewares.isAdmin],
      children: [...adminRoutes],
    },
    {
      path: '/nps',
      meta: { title: 'nps.meta_title' },
      children: [...npsRoutes],
    },
    {
      path: '/cleepo',
      beforeEnter: [
        () => {
          useAppStore().setCurrentApp(AppType.Cleepo);
        },
      ],
      children: [...cleepoRoutes],
    },
    {
      path: '/terms',
      component: () => import('../layouts/TermsLayout.vue'),
      beforeEnter: [
        (to, from, next) => {
          // Only redirect if we're at exactly /terms
          if (to.path === '/terms') {
            const { locale } = i18n.global as any;
            if (locale.value === 'es') {
              return next({ name: 'TermsEsView' });
            } else if (locale.value === 'pt') {
              return next({ name: 'TermsPtView' });
            } else {
              return next({ name: 'TermsEnView' });
            }
          }
          // Otherwise, allow the navigation to proceed
          next();
        },
      ],
      children: [
        {
          path: 'en',
          name: 'TermsEnView',
          component: () => import('../views/terms/TermsEnView.vue'),
        },
        {
          path: 'es',
          name: 'TermsEsView',
          component: () => import('../views/terms/TermsEsView.vue'),
        },
        {
          path: 'pt',
          name: 'TermsPtView',
          component: () => import('../views/terms/TermsPtView.vue'),
        },
      ],
    },
    // Redirect to /cleepo for all /geo routes
    {
      path: '/geo',
      redirect: '/cleepo',
      children: [
        {
          path: ':pathMatch(.*)*',
          redirect: '/cleepo',
        },
      ],
    },
    {
      path: '/:pathMatch(.*)*',
      meta: { title: 'not_found.title' },
      component: () => import('../layouts/BaseLayout.vue'),
      beforeEnter: [authMiddlewares.verifyAuthentication],
      children: [
        {
          path: '',
          name: 'NotFoundView',
          component: () => import('../views/common/NotFoundView.vue'),
        },
      ],
    },
  ],
});

router.afterEach(() => {
  const uiStore = useUiStore();
  uiStore.setLoading(false);
});

router.beforeEach((to, from) => {
  const lastPath = useStorage('app_last_path', '', sessionStorage);
  if (to.path === from.path) {
    return;
  }

  lastPath.value = from.path;
  return;
});

router.onError((error, to) => {
  if (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed')
  ) {
    if (!to?.fullPath) {
      window.location.reload();
    } else {
      window.location.href = to.fullPath;
    }
  }
});

export default router;
